import '@/assets/styles/main.scss';
import 'moment/min/locales';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import '@/utils/localization/i18next';

import { BASENAME, IS_ADMIN } from '@/services/Constants';
import { initializeBugsnag } from '@/services/Helper';
import { componentLoader } from '@/componentLoader';
import { store } from '@/store';

const rootContainer = document.getElementById('root');

if (!rootContainer) {
	throw new Error('Root element not found');
}

const rootElement = createRoot(rootContainer);

async function startApp() {
	const App = IS_ADMIN
		? lazy(() => componentLoader(() => import('@/admin/AdminApp'), 3))
		: lazy(() => componentLoader(() => import('@/App'), 3));

	const html = document.documentElement;

	// To prevent rendertron from injecting base tag with its origin,
	// which will results in CORS issue on loading static (js/css) files.
	// https://github.com/GoogleChrome/rendertron/blob/7999fc591901e2038f4b664e5098e6d8589da177/src/renderer.ts#L72
	const base = document.createElement('base');
	const port = window.location.port ? `:${window.location.port}` : '';
	const origin = `${window.location.protocol}//${window.location.hostname}${port}`;
	base.setAttribute('href', origin);
	document.head.append(base);

	window.addEventListener('keyboardDidShow', () => {
		html.classList.add('keyboard-on');
	});

	window.addEventListener('keyboardDidHide', () => {
		html.classList.remove('keyboard-on');
	});

	const app = (
		<Provider store={store}>
			<BrowserRouter basename={BASENAME}>
				<Suspense fallback={null}>
					<App />
				</Suspense>
			</BrowserRouter>
		</Provider>
	);

	const ErrorBoundary = initializeBugsnag();

	if (ErrorBoundary) {
		rootElement.render(<ErrorBoundary>{app}</ErrorBoundary>);
		return;
	}

	rootElement.render(app);
}

void startApp();
